import { createContext, useContext } from 'react';
import { createIdempotencyKey } from '@/utils/idempotency.js';
import { resolveAuthenticatedHeaders } from '@/utils/requestContext.js';

const API_URL =
  import.meta.env.VITE_GRAPHQL_URL ||
  `${import.meta.env.VITE_API_URL || 'https://api.sinamonsoft.ar'}/graphql`;

const DEFAULT_TIMEOUT_MS = 15000;

function buildHeaders(extraHeaders = {}) {
  return resolveAuthenticatedHeaders({
    'Content-Type': 'application/json',
    ...extraHeaders,
  });
}

function isMutationQuery(query) {
  if (typeof query === 'string') {
    return /^\s*mutation\b/i.test(query);
  }
  if (query?.definitions) {
    return query.definitions.some(
      (definition) => definition?.kind === 'OperationDefinition' && definition?.operation === 'mutation',
    );
  }
  return false;
}

function toGraphqlError(message, extensions = {}) {
  return {
    message,
    extensions,
  };
}

function mergeSignals(signalA, signalB) {
  if (!signalA) return signalB;
  if (!signalB) return signalA;

  const controller = new AbortController();

  const abort = () => {
    controller.abort();
    signalA.removeEventListener('abort', abort);
    signalB.removeEventListener('abort', abort);
  };

  if (signalA.aborted || signalB.aborted) {
    controller.abort();
    return controller.signal;
  }

  signalA.addEventListener('abort', abort, { once: true });
  signalB.addEventListener('abort', abort, { once: true });
  return controller.signal;
}

async function executeGraphql(query, variables = {}, options = {}) {
  const { signal, timeoutMs = DEFAULT_TIMEOUT_MS, fetchOptions = {} } = options;
  const extraHeaders = fetchOptions?.headers || {};
  const timeoutController = new AbortController();
  const mergedSignal = mergeSignals(signal, timeoutController.signal);

  let timeoutId = null;
  if (timeoutMs > 0) {
    timeoutId = window.setTimeout(() => {
      timeoutController.abort();
    }, timeoutMs);
  }

  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: buildHeaders({
        ...extraHeaders,
        ...(isMutationQuery(query) ? { 'x-idempotency-key': createIdempotencyKey('mutation') } : {}),
      }),
      credentials: 'include',
      signal: mergedSignal,
      body: JSON.stringify({ query, variables }),
    });

    const isJson = response.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await response.json() : { errors: [toGraphqlError(await response.text())] };

    if (!response.ok && !payload.errors?.length) {
      return {
        data: payload.data ?? null,
        errors: [toGraphqlError(`GraphQL HTTP ${response.status}`, { status: response.status })],
      };
    }

    if (response.status === 401) {
      return {
        ...payload,
        errors: [...(payload.errors || []), toGraphqlError('Sesion no autorizada para GraphQL.', { status: 401 })],
      };
    }

    return {
      data: payload.data ?? null,
      errors: payload.errors || null,
    };
  } catch (error) {
    if (error?.name === 'AbortError') {
      return {
        data: null,
        errors: [toGraphqlError('La solicitud fue cancelada.', { code: 'REQUEST_ABORTED', aborted: true })],
      };
    }

    return {
      data: null,
      errors: [toGraphqlError(error?.message || 'Error de red al ejecutar GraphQL.', { code: 'NETWORK_ERROR' })],
    };
  } finally {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
  }
}

export const simpleGqlClient = {
  url: API_URL,
  query: executeGraphql,
  mutation: executeGraphql,
};

const SimpleGraphQLContext = createContext(simpleGqlClient);

export function SimpleProvider({ children }) {
  return <SimpleGraphQLContext.Provider value={simpleGqlClient}>{children}</SimpleGraphQLContext.Provider>;
}

export function useSimpleQuery() {
  const client = useContext(SimpleGraphQLContext);
  return {
    client,
    query: client.query,
  };
}
